import { getClientCookies } from "@/lib/cookies";
import type {
  GetApplicationApiResponse,
  GetUserByIdApiResponse,
  SignInSuccess,
} from "@/services/schemas";
import { isSessionExpired } from "@/utils/cookieExpiry";
import type { StateCode } from "@brazilian-utils/brazilian-utils/dist/common/states";
import { produce } from "immer";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type UserOrganizationStateProps = {
  organization: SignInSuccess["organization"] | null;
  apps: GetApplicationApiResponse[] | null;
};

const initialState: UserOrganizationStateProps = {
  organization: null,
  apps: null,
};

type UpdateUserStatusProps = {
  id: number;
  status: GetUserByIdApiResponse["status"];
};

type UpdateOrganization = {
  name: string;
  CEP: string;
  address: string;
  city: string;
  state: StateCode;
};

type Actions = {
  updateUserStatus: ({ id, status }: UpdateUserStatusProps) => void;
  updateOrganization: (organization: UpdateOrganization) => void;
  saveOrganization: (organization: SignInSuccess["organization"]) => void;
  saveApps: (apps: GetApplicationApiResponse[]) => void;
  resetOrganization: () => void;
  resetApps: () => void;
  resetAll: () => void;
};

export const useUserOrganizationStore = create(
  persist<UserOrganizationStateProps & Actions>(
    (set) => ({
      ...initialState,
      updateUserStatus: ({ id, status }: UpdateUserStatusProps) => {
        set((prevState) =>
          produce(prevState, (draft) => {
            if (!draft.organization) return;
            draft.organization.users = draft.organization.users.map((user) =>
              user.id === id ? { ...user, status } : user,
            );
          }),
        );
      },
      saveOrganization: (organization: SignInSuccess["organization"]) => {
        set((prevState) =>
          produce(prevState, (draft) => {
            draft.organization = organization;
          }),
        );
      },

      updateOrganization: (organization: UpdateOrganization) => {
        set((prevState) =>
          produce(prevState, (draft) => {
            if (!draft.organization) return;
            draft.organization.name = organization.name;
            draft.organization.CEP = organization.CEP;
            draft.organization.address = organization.address;
            draft.organization.city = organization.city;
            draft.organization.state = organization.state;
          }),
        );
      },

      saveApps: (apps: GetApplicationApiResponse[]) => {
        set((prevState) =>
          produce(prevState, (draft) => {
            draft.apps = apps;
          }),
        );
      },
      resetOrganization: () => {
        set((prevState) =>
          produce(prevState, (draft) => {
            draft.organization = null;
          }),
        );
      },
      resetApps: () => {
        set((prevState) =>
          produce(prevState, (draft) => {
            draft.apps = [];
          }),
        );
      },
      resetAll: () => set(initialState),
    }),
    { name: "user-organization-store:v1.2.2" },
  ),
);

if (typeof window !== "undefined") {
  const cookies = getClientCookies();

  if (cookies?.created_at && isSessionExpired(cookies.created_at)) {
    useUserOrganizationStore.getState().resetAll();
  }
}
